// Shared pet-policy → bullet-list helpers.
//
// The Pet Policy page section (PetPolicy) renders the property's pet policy as
// a square-bullet list. These pure helpers build that HTML:
//  - `petPolicyToListHtml` breaks a free-form blurb into discrete <li> items.
//  - `buildStructuredPetPolicyHtml` assembles a list from the structured
//    property fields when no blurb is authored.
// Keep this dependency-free (no React / runtime imports) so it can be reused
// anywhere.

/** True for a string that carries a real, displayable value. */
export function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return (
    normalized !== '' &&
    normalized !== 'false' &&
    normalized !== 'null' &&
    normalized !== 'undefined'
  );
}

/** Plain-text of an HTML fragment (tags + &nbsp; stripped, trimmed). */
export function stripTags(s: string): string {
  return s.replace(/<[^>]+>/g, '').replace(/&nbsp;/gi, ' ').trim();
}

/**
 * Turn a pet-policy blurb into an explicit bullet list.
 *
 * The pet-policy description field is authored as free-form richtext and, in
 * practice, arrives as paragraph / <br>-separated lines rather than a real
 * <ul>. Rendered verbatim those lines collapse into an undifferentiated block,
 * so here we break them into discrete <li> items:
 *  - If the source is ALREADY a list (<li> present), it's trusted as-is.
 *  - Otherwise every paragraph / line becomes a <li>. Fully-bold lines (or
 *    short "Label:" lines) become <h4> sub-headings so a section like
 *    "Application & Admin Fees" stands apart from the bullets above it.
 *  - A leading "Pet Policy" heading is dropped, since callers print their own
 *    "Pet Policy" heading directly above this content.
 * Inline markup (<strong>, <a>, …) inside a line is preserved.
 */
export function petPolicyToListHtml(raw: string | null | undefined): string {
  if (!hasValue(raw)) return '';
  if (/<li[\s>]/i.test(raw)) return raw;

  const segments = raw
    .replace(/<\/(?:p|div|h[1-6])>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/?(?:ul|ol)[^>]*>/gi, '\n')
    .replace(/<(?:p|div|h[1-6])[^>]*>/gi, '')
    .split('\n')
    .map((s) => s.trim())
    .filter((s) => stripTags(s).length > 0);

  // A single line can't be meaningfully "broken down" — leave it untouched.
  if (segments.length <= 1) return raw;

  const parts: string[] = [];
  let listOpen = false;
  const openList = () => {
    if (!listOpen) { parts.push('<ul>'); listOpen = true; }
  };
  const closeList = () => {
    if (listOpen) { parts.push('</ul>'); listOpen = false; }
  };

  segments.forEach((seg, i) => {
    const text = stripTags(seg);
    const headingText = text.replace(/:\s*$/, '').trim();
    const fullyBold = /^(?:<(?:strong|b)\b[^>]*>)[\s\S]*<\/(?:strong|b)>$/i.test(seg);
    const shortLabel = /:\s*$/.test(text) && headingText.length <= 42;
    const isHeading = fullyBold || shortLabel;
    // Drop a leading "Pet Policy" heading — callers render one already.
    if (i === 0 && isHeading && /^pet\s*policy$/i.test(headingText)) return;
    if (isHeading) {
      closeList();
      parts.push(`<h4>${headingText}</h4>`);
    } else {
      openList();
      parts.push(`<li>${seg}</li>`);
    }
  });
  closeList();

  return parts.join('') || raw;
}

/** Trimmed string form of a property field (numbers → their string, nullish → ''). */
export function fieldStr(val: unknown): string {
  return val === null || val === undefined ? '' : String(val).trim();
}

/** True for affirmative flag fields (boolean true, "true"/"yes"/"1"). */
export function isAffirmative(val: unknown): boolean {
  if (val === true) return true;
  const s = fieldStr(val).toLowerCase();
  return s === 'true' || s === 'yes' || s === 'y' || s === '1';
}

/** "$1,250"-style whole-dollar formatting; returns the raw string if unparseable. */
export function formatMoney(val: unknown): string {
  const s = fieldStr(val);
  const num = parseFloat(s.replace(/[^0-9.]/g, ''));
  if (isNaN(num)) return s;
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

export interface PetPolicyFields {
  cats: boolean;
  dogs: boolean;
  maxPets: string;
  maxWeight: string;
  monthlyRent: string;
  fee: string;
  deposit: string;
  restrictions: string;
}

/**
 * Build a square-bullet pet-policy list from the structured property fields —
 * the fallback used when `property_pet_policy_description` is empty (or isn't
 * authored as a list). Only fields that carry a value contribute a bullet, so a
 * sparsely configured property still yields a clean list. Any pet restrictions
 * are appended under a "Restrictions" sub-heading (themselves broken into bullets).
 */
export function buildStructuredPetPolicyHtml(f: PetPolicyFields): string {
  const items: string[] = [];
  if (f.cats && f.dogs) items.push('Cats &amp; Dogs Welcome');
  else if (f.cats) items.push('Cats Welcome');
  else if (f.dogs) items.push('Dogs Welcome');
  if (f.maxPets) items.push(`${f.maxPets} Pet${f.maxPets === '1' ? '' : 's'} Per Apartment Maximum`);
  if (f.maxWeight) items.push(`${f.maxWeight} lb Weight Limit`);
  if (f.monthlyRent) items.push(`${formatMoney(f.monthlyRent)}/mo Pet Rent`);
  if (f.fee) items.push(`${formatMoney(f.fee)} Pet Fee`);
  if (f.deposit) items.push(`${formatMoney(f.deposit)} Pet Deposit`);

  const parts: string[] = [];
  if (items.length) parts.push(`<ul>${items.map((i) => `<li>${i}</li>`).join('')}</ul>`);

  if (hasValue(f.restrictions)) {
    const restrictionsList = petPolicyToListHtml(f.restrictions);
    const restrictionsHtml = /<li[\s>]/i.test(restrictionsList)
      ? restrictionsList
      : `<ul><li>${restrictionsList}</li></ul>`;
    parts.push(`<h4>Restrictions</h4>${restrictionsHtml}`);
  }

  return parts.join('');
}
